import * as React from "react"
import { useEffect } from "react"
import { useSignal } from "@preact/signals-react"
import { useSearchParams } from "react-router-dom"
import { Box, Grid, Skeleton, Typography } from "@mui/material"
import { decodingJSON, isEmpty, mergeDicts } from "@ui-seeker/utils"

import {
    commonFiltersShareValue,
    disabledCommonFiltersSubmit,
    haveEncodedValue,
    isLoadingCommonFilters,
    renderSkuGroup,
    resetCommonFilters,
    setCommonFilters,
    isSystemBusy,
    haveSkuEncodedValue
} from '@signals'
import {
    DIPAuthorizedSeller,
    DIPBrand,
    DIPBrandAsync,
    DIPBrandCustomer,
    DIPBuyBox,
    DIPCategory,
    DIPCategoryFakeSelect,
    DIPCountry,
    DIPCurrentSingleDatePicker,
    DIPFavFilters,
    DIPFavouriteFiltersModal,
    DIPGoogleCategories,
    DIPKeyword,
    DIPPrevSingleDatePicker,
    DIPDashboardSelect,
    DIPSeller,
    DIPSite,
    DIPSku,
    DIPSkuGroup,
    DIPSplitKpiSelect,
    DIPVisionSelect,
    DIPValidatedSelect,
    DIPWithShippingSwitch,
    DIPNormalizedSelect,
    DIPLostReasonSelect,
    DIPPriceDiscountSwitch,
    DIPProductInStockSwitch,
    DIPCustomer,
    DIPMetricSelect,
    DIPCompareSite,
    DIPageCategorySelect,
    DIPageProductTypeSelect,
    DIResourceTypeSelect,
    DIBannersTagsSelect,
    DIPDeviceTypeSelect,
    DIPSectionCategory
    , DIPCompany
} from "./@components"
import CommonFiltersFooterActions from "./FooterActions"
import DIPDateRange from 'Pages/@PageWrapper/Actions/DateRange'


export default function CommonFilters({ onSubmit=null, components={}, compProps={}, defaultValues = {}, isLoadingDefaultFilters=false, downloadCSV=null, ...props }) {
    const [searchParams] = useSearchParams()
    const startLaunch = useSignal(false)

    const firstRetailerOptions = useSignal([])
    const secondRetailerOptions = useSignal([])

    useEffect(() => {
        return () => {
            isLoadingCommonFilters.value = false
            disabledCommonFiltersSubmit.value = false
            isSystemBusy.value = false
        }
    }, [])

    const { myComps, myCompProps } = {
        myComps: {
            favFilters: false, buyBox: false, authorizedSeller: false,
            withShippingPrice: false, onlySingleDate: false,
            currentPrevDates: false,
            country: false, site: false,
            category: false, categoryFake: false, googleCategory: false, company: false, brand: false, brandAsync: false, skuGroup: false, sku: false,
            sectionCategory: false, pageCategory: false, pageProductType: false, resourceType: false, bannersTags: false, deviceType: false,
            dashboard: false, keyword: false, seller: false, vision: false, validated: false, normalized: false,
            splitKpi: false, favFiltersOnlyLine: false, spaceOnlySingleDate: false, actions: true, lostReason: false,
            brandCustomer: false, dateRange: false,
            productsInStock: false, priceDiscount: false, blankSpace: false, customer: false, metric: false,
            sitesCompare: false,
            ...components
        },
        myCompProps: mergeDicts({
            buyBox: { grid: { xs: 2 } },
            authorizedSeller: { grid: { xs: 3 } },
            withShippingPrice: { grid: { xs: 12, textAlign: 'end' } },
            currentSingleDate: { grid: { xs: 12, md: 2 } },
            previousSingleDate: { grid: { xs: 2 }, disabled: true },
            spaceSinglesDate: { grid: { xs: 8 } },
            dateRange: { grid: { xs: 4 } },
            spaceOnlySingleDate: { grid: { xs: 12, md: 10 } },
            country: { grid: { xs: 12, md: 3 }, multiple: true },
            sitesCompare: { grid: { xs: 12, md: 12 } },
            site: { grid: { xs: 12, md: 5 } },
            category: { grid: { xs: 12, md: 4 } },
            googleCategory: { grid: { xs: 12, md: 3 } },
            categoryFake: { grid: { xs: 12, md: 2 } },
            company: { grid: { xs: 12, md: 3 } },
            brand: { grid: { xs: 12, md: 3 } },
            brandAsync: { grid: { xs: 12, md: 3 } },
            sectionCategory: { grid: { xs: 12, md: 4 } },
            pageCategory: { grid: { xs: 12, md: 3 } },
            pageProductType: { grid: { xs: 12, md: 3 } },
            resourceType: { grid: { xs: 12, md: 3 } },
            bannersTags: { grid: { xs: 12, md: 2 } },
            deviceType: { grid: { xs: 12, md: 2 } },
            brandCustomer: { grid: { xs: 12, md: 3 } },
            skuGroup: { grid: { xs: 12, md: 3 } },
            customer: { grid: { xs: 12, md: 3 } },
            metric: { grid: { xs: 12, md: 3 } },
            sku: { grid: { xs: 12, md: 6 }, limitTags: 2, showAdornment: true },
            dashboard: { grid: { xs: 12, md: 3 } },
            keyword: { grid: { xs: 12, md: 3 } },
            seller: { grid: { xs: 12, md: 2 } },
            vision: { grid: { xs: 12, md: 3 } },
            validated: { grid: { xs: 12, md: 3 } },
            lostReason: { grid: { xs: 12, md: 3 } },
            normalized: { grid: { xs: 12, md: 3 } },
            splitKpi: { grid: { xs: 12, md: 6 } },
            actions: { grid: { xs: 12 } },
            favFilters: { grid: { xs: 12, md: 2 } },
            favFiltersOnlyLine: { grid: { xs: 12, md: 8 } },
            productsInStock: { grid: { xs: 12, textAlign: 'end' } },
            priceDiscount: { grid: { xs: 12, textAlign: 'end' } },
            blankSpace: { grid: { xs: 12 } },
        }, compProps)
    }

    useEffect(() => {
        resetCommonFilters()

        if (!isEmpty(defaultValues) && !isLoadingDefaultFilters) {
            setCommonFilters(mergeDicts(commonFiltersShareValue.value, defaultValues))
        }

        const spEncode = searchParams.get('sp')
        const launch = searchParams.get('launch')
        if(spEncode) {
            setCommonFilters(decodingJSON(spEncode))
            haveEncodedValue.value = true
            if (decodingJSON(spEncode)?.sku?.length > 0 )
                haveSkuEncodedValue.value = true
        }

        if (launch) {
            startLaunch.value = true
        }

    }, [isLoadingDefaultFilters])

    if (isLoadingDefaultFilters) return <Skeleton variant="rectangular" height={500} />

    return <Grid container spacing={2} sx={{ mt: 1 }}>
        { myComps.favFilters && <Grid item {...myCompProps?.favFilters?.grid}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <DIPFavouriteFiltersModal {...myCompProps?.favFilters} />
                <DIPFavFilters {...myCompProps?.favFilters} />
            </Box>
        </Grid> }

        { myComps.favFiltersOnlyLine && <Grid item {...myCompProps?.favFiltersOnlyLine?.grid}/> }
        { myComps.buyBox && <Grid item {...myCompProps?.buyBox?.grid}>
            <DIPBuyBox {...myCompProps?.buyBox} />
        </Grid> }

        { myComps.authorizedSeller && <Grid item {...myCompProps?.authorizedSeller?.grid}>
            <DIPAuthorizedSeller {...myCompProps?.authorizedSeller} />
        </Grid> }

        { myComps.dateRange && <Grid item {...myCompProps?.dateRange?.grid}>
            <DIPDateRange {...myCompProps?.dateRange} />
        </Grid> }

        { myComps.currentPrevDates && <>
            <Grid item {...myCompProps?.currentSingleDate?.grid}>
                <DIPCurrentSingleDatePicker {...myCompProps?.currentPrevDates} />
            </Grid>

            <Grid item {...myCompProps?.previousSingleDate?.grid}>
                <DIPPrevSingleDatePicker {...myCompProps?.previousSingleDate} />
            </Grid>

            <Grid item {...myCompProps?.spaceSinglesDate?.grid} />
        </> }

        { myComps.onlySingleDate && <>
            <Grid item {...myCompProps?.currentSingleDate?.grid}>
                <DIPCurrentSingleDatePicker {...myCompProps?.currentPrevDates} />
            </Grid>

            <Grid item {...myCompProps?.spaceOnlySingleDate?.grid} />

        </> }

        { myComps.productsInStock && <Grid item {...myCompProps?.productsInStock?.grid}>
            <DIPProductInStockSwitch {...myCompProps?.productsInStock} />
        </Grid> }

        { myComps.priceDiscount && <Grid item {...myCompProps?.priceDiscount?.grid}>
            <DIPPriceDiscountSwitch {...myCompProps?.priceDiscount} />
        </Grid> }

        { myComps.withShippingPrice && <Grid item {...myCompProps?.withShippingPrice?.grid}>
            <DIPWithShippingSwitch {...myCompProps?.withShippingPrice} />
        </Grid> }

        { myComps.blankSpace && <Grid item {...myCompProps?.blankSpace?.grid} />}

        { myComps.customer && <Grid item {...myCompProps?.customer?.grid}>
            <DIPCustomer {...myCompProps?.customer} />
        </Grid> }

        { myComps.metric && <Grid item {...myCompProps?.metric?.grid}>
            <DIPMetricSelect {...myCompProps?.metric} />
        </Grid> }

        { myComps.sitesCompare && <Grid container item alignItems="center" {...myCompProps?.sitesCompare?.grid}>
            <Grid item {...myCompProps?.sitesCompare?.retailer1Grid}>
                <DIPCompareSite
                    targetSignal={myCompProps?.sitesCompare.retailerSignal}
                    optionsSignal={firstRetailerOptions}
                    error={myCompProps?.sitesCompare?.retailerErrors}
                />
            </Grid>

            <Grid item {...myCompProps?.sitesCompare?.vsGrid} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 900,
                        fontStyle: 'italic',
                        userSelect: 'none',
                        background: theme => `linear-gradient(90deg, ${theme.palette.grey[500]} 0%, ${theme.palette.primary.main} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    VS
                </Typography>
            </Grid>

            <Grid item {...myCompProps?.sitesCompare?.retailer2Grid}>
                <DIPCompareSite
                    label={'Second Retailer'}
                    targetSignal={myCompProps?.sitesCompare.retailerSecondarySignal}
                    optionsSignal={secondRetailerOptions}
                    error={myCompProps?.sitesCompare?.secondaryRetailerErrors}
                />
            </Grid>
        </Grid> }

        { myComps.country && <Grid item {...myCompProps?.country?.grid}>
            <DIPCountry {...myCompProps?.country} />
        </Grid> }

        { myComps.site && <Grid item {...myCompProps?.site?.grid}>
            <DIPSite {...myCompProps?.site} />
        </Grid> }

        { myComps.category && <Grid item {...myCompProps?.category?.grid}>
            <DIPCategory {...myCompProps?.category} />
        </Grid> }

        { myComps.categoryFake && <Grid item {...myCompProps?.categoryFake?.grid}>
            <DIPCategoryFakeSelect {...myCompProps?.categoryFake} />
        </Grid> }

        { myComps.googleCategory && <Grid item {...myCompProps?.googleCategory?.grid}>
            <DIPGoogleCategories {...myCompProps?.googleCategory} />
        </Grid> }

        { myComps.company && <Grid item {...myCompProps?.company?.grid}>
            <DIPCompany {...myCompProps?.company} />
        </Grid> }

        { myComps.brand && <Grid item {...myCompProps?.brand?.grid}>
            <DIPBrand {...myCompProps?.brand} />
        </Grid> }

        { myComps.brandAsync && <Grid item {...myCompProps?.brandAsync?.grid}>
            <DIPBrandAsync {...myCompProps?.brandAsync} />
        </Grid> }

        { myComps.deviceType && <Grid item {...myCompProps?.deviceType?.grid}>
            <DIPDeviceTypeSelect {...myCompProps?.deviceType} />
        </Grid> }

        { myComps.sectionCategory && <Grid item {...myCompProps?.sectionCategory?.grid}>
            <DIPSectionCategory {...myCompProps?.sectionCategory} />
        </Grid> }

        { myComps.resourceType && <Grid item {...myCompProps?.resourceType?.grid}>
            <DIResourceTypeSelect {...myCompProps?.resourceType} />
        </Grid> }

        { myComps.pageProductType && <Grid item {...myCompProps?.pageProductType?.grid}>
            <DIPageProductTypeSelect {...myCompProps?.pageProductType} />
        </Grid> }

        { myComps.pageCategory && <Grid item {...myCompProps?.pageCategory?.grid}>
            <DIPageCategorySelect {...myCompProps?.pageCategory} />
        </Grid> }

        { myComps.bannersTags && <Grid item {...myCompProps?.bannersTags?.grid}>
            <DIBannersTagsSelect {...myCompProps?.bannersTags} />
        </Grid> }

        { myComps.brandCustomer && <Grid item {...myCompProps?.brandCustomer?.grid}>
            <DIPBrandCustomer {...myCompProps?.brandCustomer} />
        </Grid> }

        { myComps.skuGroup && <Grid item {...myCompProps?.skuGroup?.grid}>
            { renderSkuGroup.value && <DIPSkuGroup {...myCompProps?.skuGroup} /> }
        </Grid> }

        { myComps.sku && <Grid item {...myCompProps?.sku?.grid}>
            <DIPSku {...myCompProps?.sku} />
        </Grid> }

        { myComps.lostReason && <Grid item {...myCompProps?.lostReason?.grid}>
            <DIPLostReasonSelect {...myCompProps?.lostReason} />
        </Grid> }

        { myComps.seller && <Grid item {...myCompProps?.seller?.grid}>
            <DIPSeller {...myCompProps?.seller} />
        </Grid> }

        { myComps.dashboard && <Grid item {...myCompProps?.dashboard?.grid}>
            <DIPDashboardSelect {...myCompProps?.dashboard} />
        </Grid> }

        { myComps.keyword && <Grid item {...myCompProps?.keyword?.grid}>
            <DIPKeyword {...myCompProps?.keyword} />
        </Grid> }

        { myComps.vision && <Grid item {...myCompProps?.vision?.grid}>
            <DIPVisionSelect {...myCompProps?.vision} />
        </Grid> }

        { myComps.validated && <Grid item {...myCompProps?.validated?.grid}>
            <DIPValidatedSelect {...myCompProps?.validated} />
        </Grid> }

        { myComps.normalized && <Grid item {...myCompProps?.normalized?.grid}>
            <DIPNormalizedSelect {...myCompProps?.normalized} />
        </Grid> }

        { myComps.splitKpi && <Grid item {...myCompProps?.splitKpi?.grid}>
            <DIPSplitKpiSelect {...myCompProps?.splitKpi} />
        </Grid> }

        { myComps.actions && <Grid item {...myCompProps?.actions?.grid} {...compProps?.actions} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', ...compProps?.actions?.sx }}>
            <CommonFiltersFooterActions onSubmit={onSubmit} components={myComps} launch={startLaunch.value} downloadCSV={downloadCSV} />
        </Grid>}
    </Grid>
}